import {
  HISTORY_PLACEMENT_METADATA_KEY,
  chatMessagePartKey,
  messagePartKeys,
  type TerraUIMessage,
} from '@terra/shared-dto-chat-types';

type UserMessagePart = NonNullable<TerraUIMessage['parts']>[number];

export type UserMessagePlacement = {
  predecessorIds: readonly string[];
  partKeysByMessageId?: Readonly<Record<string, readonly string[]>>;
};

function isPredecessorList(
  value: readonly string[] | UserMessagePlacement,
): value is readonly string[] {
  return Array.isArray(value);
}

export function asPlacement(
  value: readonly string[] | UserMessagePlacement,
): UserMessagePlacement {
  if (isPredecessorList(value)) {
    return { predecessorIds: value };
  }

  return value;
}

export function historyPlacementMetadata({
  liveMessages,
}: {
  liveMessages: readonly TerraUIMessage[];
}): Record<string, unknown> | undefined {
  const partKeysByMessageId: Record<string, readonly string[]> = {
    ...openTurnPartKeysByMessageId({
      predecessors: liveMessages,
    }),
  };
  const lastUserIndex = liveMessages.reduce(
    (latest, message, index) => (message.role === 'user' ? index : latest),
    -1,
  );

  for (const [index, message] of liveMessages.entries()) {
    if (message.role !== 'assistant' || index <= lastUserIndex) {
      continue;
    }

    if (Object.hasOwn(partKeysByMessageId, message.id)) {
      continue;
    }

    partKeysByMessageId[message.id] = [];
  }

  if (Object.keys(partKeysByMessageId).length === 0) {
    return undefined;
  }

  return {
    [HISTORY_PLACEMENT_METADATA_KEY]: { partKeysByMessageId },
  };
}

export function openTurnPartKeysByMessageId({
  predecessors,
  followUpIds = new Set(),
}: {
  predecessors: readonly TerraUIMessage[];
  followUpIds?: ReadonlySet<string>;
}): Readonly<Record<string, readonly string[]>> {
  const lastUserIndex = predecessors.reduce(
    (latest, message, index) =>
      message.role === 'user' && !followUpIds.has(message.id) ? index : latest,
    -1,
  );
  const partKeysByMessageId: Record<string, readonly string[]> = {};

  for (const [index, message] of predecessors.entries()) {
    if (message.role !== 'assistant' || index <= lastUserIndex) {
      continue;
    }

    const keys = messagePartKeys(message);

    if (keys.length === 0) {
      continue;
    }

    partKeysByMessageId[message.id] = keys;
  }

  return partKeysByMessageId;
}

function partKeysSnapshotForMessage({
  placement,
  messageId,
}: {
  placement: UserMessagePlacement;
  messageId: string;
}): readonly string[] | undefined {
  const snapshot = placement.partKeysByMessageId?.[messageId];

  return snapshot && snapshot.length > 0 ? snapshot : undefined;
}

function snapshotMatchesMessagePrefix({
  snapshot,
  message,
}: {
  snapshot: readonly string[];
  message: TerraUIMessage;
}): boolean {
  const parts = message.parts ?? [];

  if (parts.length < snapshot.length) {
    return false;
  }

  return snapshot.every((key, index) => {
    const part = parts[index];

    if (!part) {
      return false;
    }

    const current = chatMessagePartKey(part);

    if (current === key) {
      return true;
    }

    const prefix = streamedTextKeyPrefix(key);

    return prefix !== undefined && current.startsWith(key);
  });
}

function streamedTextKeyPrefix(
  key: string,
): 'text:' | 'reasoning:' | undefined {
  if (key.startsWith('text:')) {
    return 'text:';
  }

  if (key.startsWith('reasoning:')) {
    return 'reasoning:';
  }

  return undefined;
}

function replacementSearchStart({
  messages,
  predecessorIds,
  lostId,
  anchoredIds,
}: {
  messages: readonly TerraUIMessage[];
  predecessorIds: readonly string[];
  lostId: string;
  anchoredIds: ReadonlySet<string>;
}): number {
  const lostIndex = predecessorIds.indexOf(lostId);
  const anchorBefore = predecessorIds
    .slice(0, lostIndex === -1 ? predecessorIds.length : lostIndex)
    .filter((id) => anchoredIds.has(id))
    .at(-1);

  return anchorBefore === undefined
    ? 0
    : messages.findIndex((message) => message.id === anchorBefore) + 1;
}

export function retargetPlacementAnchors({
  messages,
  placements,
}: {
  messages: readonly TerraUIMessage[];
  placements: ReadonlyMap<string, readonly string[] | UserMessagePlacement>;
}): Map<string, UserMessagePlacement> {
  const messageIds = new Set(messages.map((message) => message.id));
  const retargeted = new Map<string, UserMessagePlacement>();

  for (const [userMessageId, rawPlacement] of placements) {
    const placement = asPlacement(rawPlacement);
    const replacementIds = new Map<string, string>();
    const anchoredIds = new Set(
      placement.predecessorIds.filter((id) => messageIds.has(id)),
    );

    for (const [messageId, snapshot] of Object.entries(
      placement.partKeysByMessageId ?? {},
    )) {
      if (messageIds.has(messageId) || snapshot.length === 0) {
        continue;
      }

      const claimedIds = new Set(replacementIds.values());
      const match = messages
        .slice(
          replacementSearchStart({
            messages,
            predecessorIds: placement.predecessorIds,
            lostId: messageId,
            anchoredIds,
          }),
        )
        .find(
          (message) =>
            message.role === 'assistant' &&
            !anchoredIds.has(message.id) &&
            !claimedIds.has(message.id) &&
            snapshotMatchesMessagePrefix({ snapshot, message }),
        );

      if (match) {
        replacementIds.set(messageId, match.id);
      }
    }

    if (replacementIds.size === 0) {
      retargeted.set(userMessageId, placement);
      continue;
    }

    const partKeysByMessageId = Object.fromEntries(
      Object.entries(placement.partKeysByMessageId ?? {}).map(
        ([messageId, snapshot]) => [
          replacementIds.get(messageId) ?? messageId,
          snapshot,
        ],
      ),
    );

    retargeted.set(userMessageId, {
      predecessorIds: placement.predecessorIds.map(
        (messageId) => replacementIds.get(messageId) ?? messageId,
      ),
      partKeysByMessageId,
    });
  }

  return retargeted;
}

export function relocatePartsAfterAnchors({
  messages,
  placements,
}: {
  messages: TerraUIMessage[];
  placements: ReadonlyMap<string, readonly string[] | UserMessagePlacement>;
}): TerraUIMessage[] {
  const anchors = [...placements.entries()].map(
    ([messageId, placement]) => [messageId, asPlacement(placement)] as const,
  );
  const overflowByUserId = new Map<string, UserMessagePart[]>();
  const emptiedIds = new Set<string>();

  const rewritten = messages.map((message) => {
    const anchorsByPartCount = anchors
      .map(([userMessageId, placement]) => ({
        userMessageId,
        snapshot: partKeysSnapshotForMessage({
          placement,
          messageId: message.id,
        }),
      }))
      .filter(
        (
          anchor,
        ): anchor is { userMessageId: string; snapshot: readonly string[] } =>
          anchor.snapshot !== undefined,
      )
      .map(({ userMessageId, snapshot }) => ({
        userMessageId,
        sentAfterPartCount: snapshot.length,
      }))
      .sort((a, b) => a.sentAfterPartCount - b.sentAfterPartCount);

    if (anchorsByPartCount.length === 0) {
      return message;
    }

    const kept: UserMessagePart[] = [];
    const parts = message.parts ?? [];

    parts.forEach((part, index) => {
      let destination: string | undefined;

      for (const { userMessageId, sentAfterPartCount } of anchorsByPartCount) {
        if (index >= sentAfterPartCount) {
          destination = userMessageId;
        }
      }

      if (destination === undefined) {
        kept.push(part);

        return;
      }

      overflowByUserId.set(destination, [
        ...(overflowByUserId.get(destination) ?? []),
        part,
      ]);
    });

    if (kept.length === parts.length) {
      return message;
    }

    if (kept.length === 0) {
      emptiedIds.add(message.id);
    }

    return { ...message, parts: kept };
  });

  if (overflowByUserId.size === 0) {
    return rewritten;
  }

  const result: TerraUIMessage[] = [];

  for (const message of rewritten) {
    if (emptiedIds.has(message.id)) {
      continue;
    }

    result.push(message);
    const overflow = overflowByUserId.get(message.id);

    if (!overflow?.length) {
      continue;
    }

    const laterKeys = new Set(
      rewritten
        .slice(rewritten.indexOf(message) + 1)
        .flatMap((item) => messagePartKeys(item)),
    );
    const unseen = overflow.filter(
      (part) => !laterKeys.has(chatMessagePartKey(part)),
    );

    if (unseen.length === 0) {
      continue;
    }

    result.push({
      id: `after-user:${message.id}`,
      role: 'assistant',
      parts: unseen,
    });
  }

  return result;
}
